import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { Runner, UpdateRunnerDto } from '../types/runner';

export function useRunners() {
  return useQuery<Runner[]>({
    queryKey: ['runners'],
    queryFn: async () => {
      const res = await api.get('/api/runners');
      return res.data;
    },
    refetchInterval: 15000,
  });
}

export function useRunner(id: string | undefined) {
  return useQuery<Runner>({
    queryKey: ['runners', id],
    queryFn: async () => {
      const res = await api.get(`/api/runners/${id}`);
      return res.data;
    },
    enabled: !!id,
    refetchInterval: 10000,
  });
}

export function useUpdateRunner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateRunnerDto & { id: string }) => {
      const res = await api.patch(`/api/runners/${id}`, data);
      return res.data;
    },
    onSuccess: (data: Runner) => {
      queryClient.invalidateQueries({ queryKey: ['runners', data.id] });
      queryClient.invalidateQueries({ queryKey: ['runners'] });
    },
  });
}

export function useDeleteRunner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/runners/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runners'] });
    },
  });
}

export function useRunnerConfig(id: string | undefined) {
  return useQuery<{ config: Record<string, unknown> | null; status: string; lastSeenAt: string | null }>({
    queryKey: ['runners', id, 'config'],
    queryFn: async () => {
      const res = await api.get(`/api/runners/${id}/config`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useUpdateRunnerConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, config }: { id: string; config: Record<string, unknown> }) => {
      const res = await api.put(`/api/runners/${id}/config`, config);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['runners', variables.id, 'config'] });
    },
  });
}

export function useRunnerInstallToken() {
  return useMutation<
    { token: string; installCommand: string; expiresAt: string; apiKeyName: string },
    Error
  >({
    mutationFn: async () => {
      const res = await api.post('/api/runners/install-token');
      return res.data;
    },
  });
}
